import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  Sparkles,
  CheckCircle,
  Loader2,
} from "lucide-react";
import api from "../api/client";

function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Invalid or missing verification token");
      setLoading(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        await api.get(`/api/v1/auth/verify-email?token=${token}`);
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.detail || "Failed to verify email");
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-2">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-xl opacity-50 animate-pulse" />
              <Activity className="w-12 h-12 text-cyan-400 relative" />
            </div>
            <span className="text-3xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              FrankTech
            </span>
            <Sparkles className="w-4 h-4 text-cyan-400" />
          </div>
          <p className="text-gray-400">Email Verification</p>
        </div>

        <div className="bg-gradient-to-br from-gray-900/80 to-gray-950/80 backdrop-blur-sm border border-gray-800/50 rounded-2xl p-8 shadow-xl shadow-cyan-500/5">
          {loading ? (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Verifying your email...</p>
            </div>
          ) : success ? (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-500/20 border border-green-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Email Verified! 🎉
              </h3>
              <p className="text-gray-400 mt-2">
                Your email has been verified successfully.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block py-3 px-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-medium rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-200"
              >
                Go to Login
              </Link>
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white">
                Verification Failed
              </h3>
              <p className="text-gray-400 mt-2">{error}</p>
              <p className="text-sm text-gray-500 mt-4">
                The verification link may have expired or is invalid.
              </p>
              <Link
                to="/login"
                className="mt-6 inline-block text-cyan-400 hover:text-cyan-300 transition-colors duration-200"
              >
                Return to Login
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default VerifyEmail;
